import React, { useEffect, useState } from 'react'

import { Alert, Button, Card, Col, Divider, Dropdown, Menu, Row, Skeleton, Table, Tooltip } from 'antd'
import classNames from 'classnames'

import benefitPlanPeriods, {
  PlanPeriodDto,
  getActivePlanPeriod,
} from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import getTaxSavings, { TaxSavingsType } from 'core/application/expenses/taxSavings/getTaxSavings'
import getTaxSavingsTransactionDetails from 'core/application/expenses/taxSavings/getTaxSavingsTransactionDetails'
import getTaxSavingsTransactionSummary from 'core/application/expenses/taxSavings/getTaxSavingsTransactionSummary'

import TaxAdvantage from 'common/components/benefit/Expenses/TaxAdvantageExpense'
import TaxFree from 'common/components/benefit/Expenses/TaxFreeExpense'
import Icon from 'common/components/general/Icon'

import useBreakPoints from 'common/hooks/useBreakPoints'
import useQueryRequest from 'common/hooks/useQueryRequest'

import { parseZone } from '../../../common/dateFormatters'
import ExpenseCoverageModal from './common/ExpenseCoverageModal'
import {
  taxSavingsColumns,
  taxSavingsTransactionDetailsColumns,
  taxSavingsTransactionSummaryColumns,
} from './common/columns'
import { ModalTaxSaving } from './common/interfaces'

import './index.less'

const TaxSavings = () => {
  const [planPeriod, planPeriodLoading] = useQueryRequest(null, () => getActivePlanPeriod())

  const [showMore, setShowMore] = useState(false)
  const [isCoverageModalVisible, setIsCoverageModalVisible] = useState(false)
  const [selectedTaxSaving, setSelectedTaxSaving] = useState<ModalTaxSaving>(null)
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto>(null)

  const [planPeriods] = useQueryRequest<PlanPeriodDto[]>([], () => benefitPlanPeriods())

  const [taxSavings, taxSavingsAreLoading, sendGetTaxSavings] = useQueryRequest<{
    months: string[]
    users: TaxSavingsType[]
  }>(null)
  const [taxSavingsTransactionSummary, taxSavingsTransactionSummaryAreLoading, sendGetTaxSavingsTransactionSummary] =
    useQueryRequest(null)
  const [taxSavingsTransactionDetails, taxSavingsTransactionDetailsAreLoading, sendGetTaxSavingsTransactionDetails] =
    useQueryRequest(null)

  const { isMd } = useBreakPoints()
  const period = selectedPlanPeriod ? selectedPlanPeriod : planPeriod
  const selectedYear = period && parseZone(period.startDate).year()

  const handleYearValue = (e: { key: React.SetStateAction<any> }) => {
    const selectPeriod = planPeriods.find((p) => p.id === e.key)
    setSelectedPlanPeriod(selectPeriod)
  }

  const handleCoverageModalVisible = (item: TaxSavingsType, colKey: number) => {
    setIsCoverageModalVisible(true)
    setSelectedTaxSaving({ user: item, colKey })
  }

  const menu = (
    <Menu onClick={handleYearValue}>
      {planPeriods.map((item) => {
        const year = parseZone(item.startDate).year()
        return (
          <Menu.Item key={item.id}>
            <span>{year}</span>
          </Menu.Item>
        )
      })}
    </Menu>
  )

  useEffect(() => {
    if (period) {
      sendGetTaxSavings(() => getTaxSavings(period.id))
      sendGetTaxSavingsTransactionSummary(() => getTaxSavingsTransactionSummary(period.id))
      sendGetTaxSavingsTransactionDetails(() => getTaxSavingsTransactionDetails(period.id))
    }
  }, [period])

  return (
    <Card title={<h5 className="mb-0">Health benefit tax savings</h5>}>
      <Skeleton loading={planPeriodLoading}>
        <Row className="semi-bold color-grey-9 font-16 mt-8 mb-16">Maximize your tax savings, every dollar counts!</Row>
        <Row className="color-grey-8 font-14 mb-32">
          <Col span={16}>
            Here you can edit whether or not you and other family members were covered by insurance providing minimum
            essential coverage (MEC). Tax savings on your health reimbursements are maximized when you are covered by
            MEC.
          </Col>
        </Row>
        <Row className="tax-savings-header mb-16">
          <div className="tax-savings-header-left">
            <div className="bg-gray-2 tax-savings-header-left-plan pl-8 pr-8">Benefit plan year</div>
            <Dropdown overlay={menu} trigger={['click']} placement="bottomLeft">
              <Button
                className="ant-dropdown-link grey-link ml-8 personal-dropdown"
                onClick={(e) => e.preventDefault()}
              >
                {selectedYear}
                <Icon type="far fa-angle-down" className="ml-4" fontAwesome />
              </Button>
            </Dropdown>
          </div>
          <div
            className={classNames({
              'flex mt-0 tax-savings-header-right-width': isMd,
              'tax-savings-header-right': true,
            })}
          >
            <div>Reimbursements incurred in a given month will be:</div>
            <TaxFree hasSimpleText />
            <div className="flex align-center">
              <TaxAdvantage hasSimpleText />
              <Tooltip
                title={
                  <div>
                    <div className="mb-8">All reimbursements are tax-advantaged , or free from payroll tax.</div>
                    <div>Family members covered by minimum essential coverage can be reimbursed tax-free.</div>
                  </div>
                }
              >
                <Icon type="far fa-question-circle" fontAwesome className="color-blue-6" />
              </Tooltip>
            </div>
          </div>
        </Row>
        {!period?.active && (
          <Row className="tax-savings-alert mb-24">
            <Alert
              message={`This years reports (${selectedYear}) have been finalized. If you need to make changes, please work with your admin so they can help make any manual adjustments needed.`}
              type="info"
              showIcon
            />
          </Row>
        )}
        <Row className="mb-32">
          <Table
            className="tax-savings-table t-savings-table"
            loading={taxSavingsAreLoading}
            dataSource={taxSavings?.users}
            columns={taxSavingsColumns(handleCoverageModalVisible, taxSavings?.months, !period?.active, isMd)}
            pagination={false}
            bordered
            scroll={{ x: 'max-content' }}
          />
        </Row>
        <Row className="mb-16">
          <div className="semi-bold color-grey-9 font-16">Transaction summary</div>
          <Divider />
          <Table
            className="tax-savings-table t-tax-savings-table"
            loading={taxSavingsTransactionSummaryAreLoading}
            dataSource={taxSavingsTransactionSummary}
            columns={taxSavingsTransactionSummaryColumns}
            pagination={false}
            scroll={{ x: 1000 }}
          />
        </Row>
        {selectedTaxSaving && (
          <ExpenseCoverageModal
            isVisible={isCoverageModalVisible}
            onClose={() => {
              setIsCoverageModalVisible(false)
              setSelectedTaxSaving(null)
            }}
            onFinish={() => {
              setIsCoverageModalVisible(false)
              setSelectedTaxSaving(null)
              sendGetTaxSavings(() => getTaxSavings(period.id))
            }}
            taxSaving={selectedTaxSaving}
            year={selectedYear}
          />
        )}
        <Row>
          <div className="tax-savings-view-more">
            <div
              className="tax-savings-view-more-button color-green-6 t-tax-savings-view-more-button"
              onClick={() => setShowMore((previous) => !previous)}
            >
              <div className="tax-savings-view-more-text">See all transaction details</div>
              {showMore ? (
                <Icon type="far fa-angle-up" className="ml-4" fontAwesome />
              ) : (
                <Icon type="far fa-angle-down" className="ml-4" fontAwesome />
              )}
            </div>
          </div>
          {showMore && (
            <Table
              className="tax-savings-table mt-16"
              loading={taxSavingsTransactionDetailsAreLoading}
              dataSource={taxSavingsTransactionDetails}
              columns={taxSavingsTransactionDetailsColumns}
              pagination={false}
              scroll={{ x: 1000 }}
            />
          )}
        </Row>
      </Skeleton>
    </Card>
  )
}

export default TaxSavings
